import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowLeft, CreditCard, Loader2, CheckCircle2, ShieldCheck, Lock, BadgeCheck } from 'lucide-react';
import { toast } from 'sonner';

const CheckoutNew = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planId = searchParams.get('plan') || 'professional';
  
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  const [formData, setFormData] = useState({
    websiteUrl: '',
    keywords: [''],
    imageUrl: '',
    youtubeUrl: '',
    articleContent: ''
  });
  
  const [wordCount, setWordCount] = useState(0);
  const MAX_WORDS = 400;

  useEffect(() => {
    fetchPlan();
  }, [planId]);

  const fetchPlan = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      const selectedPlan = response.data.find(p => p.id === planId);
      setPlan(selectedPlan || response.data[1]);
      
      // Initialize keywords based on plan
      const keywordCount = selectedPlan?.id === 'basic' ? 1 : selectedPlan?.id === 'professional' ? 3 : 10;
      setFormData(prev => ({
        ...prev,
        keywords: Array(keywordCount).fill('')
      }));
    } catch (error) {
      toast.error('Failed to load plan details');
    } finally {
      setLoading(false);
    }
  };

  const handleKeywordChange = (index, value) => {
    const newKeywords = [...formData.keywords];
    newKeywords[index] = value;
    setFormData({ ...formData, keywords: newKeywords });
  };

  const countWords = (text) => {
    if (!text || text.trim() === '') return 0;
    return text.trim().split(/\s+/).length;
  };

  const handleArticleContentChange = (e) => {
    const content = e.target.value;
    const words = countWords(content);
    
    if (words <= MAX_WORDS) {
      setFormData({ ...formData, articleContent: content });
      setWordCount(words);
    } else {
      toast.error(`Article content exceeds ${MAX_WORDS} word limit`);
    }
  };

  const validateForm = () => {
    if (!formData.websiteUrl.trim()) {
      toast.error('Please enter your website URL');
      return false;
    }
    
    try {
      new URL(formData.websiteUrl);
    } catch {
      toast.error('Please enter a valid website URL (e.g., https://example.com)');
      return false;
    }
    
    if (!formData.keywords[0]?.trim()) {
      toast.error('Please enter at least one keyword');
      return false;
    }

    if (!termsAccepted) {
      toast.error('Please accept the Terms & Conditions');
      return false;
    }

    if (formData.articleContent && countWords(formData.articleContent) > MAX_WORDS) {
      toast.error(`Article content must not exceed ${MAX_WORDS} words`);
      return false;
    }
    
    return true;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      
      const existingScript = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
      if (existingScript) {
        existingScript.onload = () => resolve(true);
        existingScript.onerror = () => resolve(false);
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (!validateForm()) return;
    
    setProcessing(true);
    
    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Failed to load payment gateway. Please check your connection.');
        setProcessing(false);
        return;
      }

      const token = localStorage.getItem('token');
      const filteredKeywords = formData.keywords.filter(k => k.trim());
      
      // Create Razorpay order
      const response = await axios.post(
        `${API}/payment/razorpay/create-order`,
        {
          plan_id: plan.id,
          origin_url: window.location.origin,
          order_details: {
            website_url: formData.websiteUrl,
            keywords: filteredKeywords,
            image_url: formData.imageUrl || null,
            youtube_url: formData.youtubeUrl || null,
            article_content: formData.articleContent || null
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const options = {
        key: response.data.razorpay_key_id,
        amount: response.data.amount,
        currency: response.data.currency,
        order_id: response.data.razorpay_order_id,
        name: 'Alignk',
        description: `${plan.name} Plan - ${plan.backlinks} Quality Backlinks`,
        image: 'https://customer-assets.emergentagent.com/job_release-nexus/artifacts/a0bdi5ug_Alignk%20backlink%20professional.png',
        prefill: {
          name: response.data.user.name,
          email: response.data.user.email
        },
        theme: {
          color: '#4AC5DB'
        },
        handler: async function (paymentResponse) {
          try {
            await axios.post(
              `${API}/payment/razorpay/verify`,
              {
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature
              },
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            );
            
            toast.success('Payment successful! Redirecting...');
            setTimeout(() => navigate('/dashboard'), 1000);
          } catch (error) {
            toast.error('Payment verification failed. Please contact support.');
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: function() {
            setProcessing(false);
            toast.error('Payment cancelled');
          }
        }
      };

      if (!window.Razorpay) {
        throw new Error('Razorpay is not loaded');
      }
      
      const paymentObject = new window.Razorpay(options);
      
      // Watch for Razorpay modal creation and fix visibility
      const modalObserver = new MutationObserver((mutations) => {
        const razorpayModal = document.querySelector('.razorpay-container');
        if (razorpayModal) {
          razorpayModal.style.setProperty('display', 'block', 'important');
          razorpayModal.style.setProperty('visibility', 'visible', 'important');
          razorpayModal.style.setProperty('opacity', '1', 'important');
          razorpayModal.style.setProperty('z-index', '9999999', 'important');
          console.log('Razorpay modal visibility fixed with !important');
          modalObserver.disconnect();
        }
      });
      
      modalObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
      
      paymentObject.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to initiate payment');
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#4AC5DB' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4 hover:bg-transparent"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Playfair Display' }}>
            Complete Your Order
          </h1>
          <p className="text-gray-600">Secure checkout powered by Razorpay</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Form - Left Side */}
          <div className="lg:col-span-2 space-y-6">
            {/* Plan Summary Card */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="text-xl">Selected Plan</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Playfair Display' }}>
                      {plan?.name} Plan
                    </h3>
                    <div className="flex gap-4 text-sm text-gray-600 mb-3">
                      <span>{plan?.backlinks} Quality Backlinks</span>
                      <span>•</span>
                      <span>{plan?.da_range} Reach</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {plan?.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center text-sm text-gray-700 bg-cyan-50 px-3 py-1 rounded-full">
                          <CheckCircle2 className="h-3 w-3 mr-1 text-green-600" />
                          {feature}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold" style={{ color: '#4AC5DB' }}>
                      ${plan?.price}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Order Details Form */}
            <Card>
              <CardHeader>
                <CardTitle>Order Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Website URL */}
                <div>
                  <Label htmlFor="websiteUrl" className="text-base">
                    Website URL <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="websiteUrl"
                    type="url"
                    placeholder="https://example.com"
                    value={formData.websiteUrl}
                    onChange={(e) => setFormData({ ...formData, websiteUrl: e.target.value })}
                    className="mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter your website URL where backlinks will point to</p>
                </div>

                {/* Keywords */}
                <div>
                  <Label className="text-base">
                    Target Keywords <span className="text-red-500">*</span>
                    <span className="text-sm font-normal text-gray-500 ml-2">
                      (Up to {plan?.id === 'basic' ? '1' : plan?.id === 'professional' ? '3' : '10'} keywords)
                    </span>
                  </Label>
                  <div className="space-y-2 mt-2">
                    {formData.keywords.map((keyword, index) => (
                      <Input
                        key={index}
                        type="text"
                        placeholder={`Keyword ${index + 1}${index === 0 ? ' (required)' : ' (optional)'}`}
                        value={keyword}
                        onChange={(e) => handleKeywordChange(index, e.target.value)}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Keywords relevant to your press release content</p>
                </div>

                {/* Optional Fields */}
                <div className="pt-4 border-t">
                  <h4 className="font-semibold mb-3">Additional Information (Optional)</h4>
                  
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="imageUrl">Image URL</Label>
                      <Input
                        id="imageUrl"
                        type="url"
                        placeholder="https://example.com/image.jpg"
                        value={formData.imageUrl}
                        onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="youtubeUrl">YouTube Video URL</Label>
                      <Input
                        id="youtubeUrl"
                        type="url"
                        placeholder="https://youtube.com/watch?v=..."
                        value={formData.youtubeUrl}
                        onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <Label htmlFor="articleContent">
                          Article Content 
                          <span className="text-xs font-normal text-gray-500 ml-2">(or post link to article)</span>
                        </Label>
                        <span 
                          className={`text-xs ${wordCount > MAX_WORDS ? 'text-red-600 font-semibold' : wordCount > MAX_WORDS * 0.9 ? 'text-orange-600' : 'text-gray-500'}`}
                        >
                          {wordCount} / {MAX_WORDS} words
                        </span>
                      </div>
                      <Textarea
                        id="articleContent"
                        placeholder="Provide your own content, paste article link, or leave blank for our team to create... (Max 400 words)"
                        value={formData.articleContent}
                        onChange={handleArticleContentChange}
                        rows={5}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        💡 You can paste article content (up to 400 words) or provide a link to your existing article
                      </p>
                      {wordCount > MAX_WORDS * 0.9 && wordCount <= MAX_WORDS && (
                        <p className="text-xs text-orange-600 mt-1">
                          ⚠️ Approaching word limit ({MAX_WORDS - wordCount} words remaining)
                        </p>
                      )}
                      {wordCount > MAX_WORDS && (
                        <p className="text-xs text-red-600 mt-1 font-semibold">
                          ❌ Exceeds word limit by {wordCount - MAX_WORDS} words. Please reduce.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Payment Summary - Right Side */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 space-y-4">
              {/* User Info Card */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Account</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">Name:</span>
                    <p className="font-medium">{user?.name}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <p className="font-medium">{user?.email}</p>
                  </div>
                </CardContent>
              </Card>

              {/* Payment Summary Card */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Plan:</span>
                    <span className="font-medium">{plan?.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Media Placements:</span>
                    <span className="font-medium">{plan?.backlinks}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Publication Quality:</span>
                    <span className="font-medium">{plan?.da_range}</span>
                  </div>
                  
                  <div className="border-t pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-lg">Total:</span>
                      <span className="font-bold text-2xl" style={{ color: '#4AC5DB' }}>
                        ${plan?.price}
                      </span>
                    </div>
                  </div>

                  {/* Terms Checkbox */}
                  <div className="flex items-start space-x-2 pt-3 border-t">
                    <Checkbox 
                      id="terms" 
                      checked={termsAccepted}
                      onCheckedChange={setTermsAccepted}
                      className="mt-1"
                    />
                    <label
                      htmlFor="terms"
                      className="text-xs text-gray-600 leading-relaxed cursor-pointer"
                    >
                      I agree to the{' '}
                      <a href="/terms-of-use" target="_blank" className="underline hover:text-cyan-600">
                        Terms & Conditions
                      </a>
                      {' '}and{' '}
                      <a href="/refund-policy" target="_blank" className="underline hover:text-cyan-600">
                        Refund Policy
                      </a>
                    </label>
                  </div>

                  {/* Payment Button */}
                  <Button
                    onClick={handlePayment}
                    disabled={processing || !termsAccepted}
                    className="w-full h-12 text-base font-semibold hover:opacity-90 transition-opacity"
                    style={{ backgroundColor: '#4AC5DB' }}
                  >
                    {processing ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="mr-2 h-5 w-5" />
                        Pay with Razorpay
                      </>
                    )}
                  </Button>

                  {/* Security Badges */}
                  <div className="pt-4 border-t border-gray-200 mt-4">
                    <div className="flex flex-wrap items-center justify-center gap-4 mb-3">
                      <div className="flex items-center gap-1.5 text-xs text-gray-600 bg-gray-50 px-3 py-1.5 rounded-full">
                        <Lock className="h-3.5 w-3.5 text-green-600" />
                        <span>256-bit SSL Encrypted</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-gray-600 bg-gray-50 px-3 py-1.5 rounded-full">
                        <ShieldCheck className="h-3.5 w-3.5 text-green-600" />
                        <span>PCI DSS Compliant</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-gray-600 bg-gray-50 px-3 py-1.5 rounded-full">
                        <BadgeCheck className="h-3.5 w-3.5 text-blue-600" />
                        <span>Verified by Razorpay</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                      <ShieldCheck className="h-4 w-4 text-green-600" />
                      <span>Your payment information is secure and encrypted</span>
                    </div>
                    <div className="flex items-center justify-center gap-3 mt-3 opacity-60">
                      <img src="https://razorpay.com/assets/razorpay-logo.svg" alt="Razorpay" className="h-5" />
                      <span className="text-xs text-gray-400">|</span>
                      <span className="text-xs text-gray-500">Trusted by 10M+ businesses</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutNew;
